import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { navPaths, toInfoSynthesisSessionPath } from "@/core/constants"
import { useSynthesisSessions } from "./hooks/use-synthesis-sessions"
import { useSynthesisSession } from "./hooks/use-synthesis-session"
import { useCreateSynthesisSession } from "./hooks/use-create-synthesis-session"
import { useUpdateSynthesisSession } from "./hooks/use-update-synthesis-session"
import { useDeleteSynthesisSession } from "./hooks/use-delete-synthesis-session"
import { DeleteSessionDialog } from "./components/delete-session-dialog"
import { SessionSidebar } from "./components/session-sidebar"
import { SynthesisSessionWorkspace } from "./components/synthesis-session-workspace"
import { SynthesisStartConversation } from "./components/synthesis-start-conversation"
import type {
  SynthesisSessionSummary,
  UpdateSynthesisSessionInput,
} from "../domain/entities/synthesis-session"
import type { NewSessionConfiguration } from "./components/synthesis-start-conversation"
import { toast } from "sonner"

export const InformationSynthesisPage = () => {
  const { sessionId: sessionIdParam } = useParams<{
    sessionId: string
  }>()
  const sessionId = sessionIdParam
  const navigate = useNavigate()

  const [sidebarExpanded, setSidebarExpanded] = useState(true)

  const { data: list, isLoading: listLoading } = useSynthesisSessions()
  const { data: session, isLoading: sessionLoading, error: sessionError } =
    useSynthesisSession(sessionId)
  const { mutate: create, isPending: isCreating } = useCreateSynthesisSession()
  const { mutate: update, isPending: isUpdating } = useUpdateSynthesisSession()
  const { mutate: del, isPending: isDeleting } = useDeleteSynthesisSession()

  const [newContent, setNewContent] = useState("")
  const [deleteTarget, setDeleteTarget] =
    useState<SynthesisSessionSummary | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [startConversationKey, setStartConversationKey] = useState(0)

  const handleNewChat = () => {
    setNewContent("")
    setStartConversationKey((k) => k + 1)
    navigate(navPaths.infoSynthesis)
  }

  const handleCreate = (configuration: NewSessionConfiguration) => {
    const t = newContent.trim()
    if (!t) return
    create(
      {
        content: t,
        configuration: {
          model: configuration.model || undefined,
          knowledgeBaseIds:
            configuration.knowledgeBaseIds.length > 0
              ? configuration.knowledgeBaseIds
              : undefined,
          enableWebSearch: configuration.enableWebSearch,
        },
      },
      {
        onSuccess: (s) => {
          setNewContent("")
          navigate(toInfoSynthesisSessionPath(s.id), {
            state: { autoAsk: { content: t } },
          })
        },
        onError: (e) => {
          toast.error(e instanceof Error ? e.message : "Could not start chat")
        },
      },
    )
  }

  const handleUpdate = (input: UpdateSynthesisSessionInput) => {
    if (!sessionId) return
    update(
      { id: sessionId, input },
      {
        onSuccess: () => {
          setSettingsOpen(false)
          toast.success("Settings saved")
        },
        onError: (e) => {
          toast.error(e instanceof Error ? e.message : "Could not save")
        },
      },
    )
  }

  const handleDelete = (id: string) => {
    const wasCurrent = id === sessionId
    if (wasCurrent) {
      navigate(navPaths.infoSynthesis)
    }
    del(id, {
      onSuccess: () => {
        setDeleteTarget(null)
        toast.success("Conversation deleted")
      },
      onError: (e) => {
        if (wasCurrent) {
          navigate(toInfoSynthesisSessionPath(id), { replace: true })
        }
        toast.error(e instanceof Error ? e.message : "Could not delete")
      },
    })
  }

  const items = list ?? []
  const queryError =
    sessionError == null
      ? null
      : sessionError instanceof Error
        ? sessionError
        : new Error(String(sessionError))

  return (
    <div className="flex h-[100dvh] min-h-0 w-full max-w-full flex-1">
      <SessionSidebar
        expanded={sidebarExpanded}
        onToggleExpanded={() => setSidebarExpanded((v) => !v)}
        listLoading={listLoading}
        items={items}
        currentSessionId={sessionId}
        onNewChat={handleNewChat}
        onRequestDelete={setDeleteTarget}
      />

      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        {sessionId ? (
          <SynthesisSessionWorkspace
            session={session}
            sessionId={sessionId}
            sessionLoading={sessionLoading}
            sessionError={queryError}
            settingsOpen={settingsOpen}
            onOpenSettings={() => setSettingsOpen(true)}
            onCloseSettings={() => setSettingsOpen(false)}
            onSaveSettings={handleUpdate}
            isUpdating={isUpdating}
          />
        ) : (
          <SynthesisStartConversation
            key={startConversationKey}
            value={newContent}
            onChange={setNewContent}
            onStart={handleCreate}
            busy={isCreating}
            isCreating={isCreating}
          />
        )}
      </section>

      <DeleteSessionDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />
    </div>
  )
}
